import { useState } from 'react';
import { Box, Typography, Chip, Paper, alpha } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import chatEvents from '../../lib/chatEvents';

/**
 * Renders a question with clickable options. On selection it dispatches a
 * `question_answer` event on the chatEvents bus carrying the plain-text answer;
 * a listener inside the assistant-ui runtime then appends it as a new user
 * message, so the LLM resumes with full context.
 *
 * Props (match the `question_card` SSE part):
 *   question   : string
 *   description: string (optional)
 *   options    : [{ label, description? }]
 *   multiple   : boolean
 *   cardId     : string
 *   disabled   : boolean (history / already-answered view)
 *   answeredText : string (optional, when replayed from history)
 */
export default function QuestionCard({
  question,
  description,
  options = [],
  multiple = false,
  cardId,
  disabled = false,
  answeredText = null,
}) {
  const [selected, setSelected] = useState(() => (multiple ? new Set() : null));
  const [submitted, setSubmitted] = useState(false);

  const isDone = disabled || submitted;

  const sendAnswer = (labels) => {
    const text = labels.length === 0 ? '' : `Selected: ${labels.join(', ')}`;
    setSubmitted(true);
    chatEvents.dispatchEvent(
      new CustomEvent('question_answer', { detail: { text, cardId, labels } })
    );
  };

  const handleClick = (label) => {
    if (isDone) return;
    if (!multiple) {
      sendAnswer([label]);
      return;
    }
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const handleSubmitMulti = () => {
    if (isDone) return;
    const labels = Array.from(selected);
    if (labels.length === 0) return;
    sendAnswer(labels);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        my: 1.5,
        p: 2.25,
        border: (t) => `1px solid ${t.palette.divider}`,
        borderRadius: 3,
        background: (t) =>
          t.palette.mode === 'dark'
            ? `linear-gradient(180deg, ${alpha(t.palette.background.paper, 0.6)} 0%, ${alpha(t.palette.background.paper, 0.2)} 100%)`
            : `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.025)} 0%, ${alpha(t.palette.background.paper, 0.9)} 100%)`,
        backdropFilter: 'blur(2px)',
      }}
    >
      {question && (
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, lineHeight: 1.35, mb: description ? 0.5 : 1.75 }}
        >
          {question}
        </Typography>
      )}
      {description && (
        <Box
          sx={{
            mb: 1.75,
            px: 1.5,
            py: 1.25,
            borderRadius: 2,
            borderLeft: (t) => `3px solid ${alpha(t.palette.primary.main, 0.45)}`,
            background: (t) => alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.08 : 0.04),
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'text.primary',
              opacity: 0.85,
              lineHeight: 1.6,
              whiteSpace: 'pre-line',
            }}
          >
            {description}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {options.map((opt, i) => {
          const label = typeof opt === 'string' ? opt : opt.label;
          const desc = typeof opt === 'string' ? null : opt.description;
          const active = multiple ? selected.has(label) : false;
          return (
            <Box
              key={`${cardId}-${i}`}
              role="button"
              tabIndex={isDone ? -1 : 0}
              onClick={() => handleClick(label)}
              onKeyDown={(e) => {
                if (isDone) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClick(label);
                }
              }}
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.25,
                p: 1.5,
                pl: 1.75,
                borderRadius: 2,
                cursor: isDone ? 'default' : 'pointer',
                userSelect: 'none',
                border: (t) =>
                  `1.5px solid ${active ? t.palette.primary.main : t.palette.divider}`,
                background: (t) =>
                  active
                    ? alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.16 : 0.08)
                    : t.palette.background.paper,
                transition: 'all 0.18s cubic-bezier(0.4, 0, 0.2, 1)',
                opacity: isDone && !active ? 0.55 : 1,
                // colored accent bar on the left
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 8,
                  bottom: 8,
                  width: 3,
                  borderRadius: 3,
                  background: (t) => t.palette.primary.main,
                  opacity: active ? 1 : 0,
                  transition: 'opacity 0.18s ease',
                },
                ...(!isDone && {
                  '&:hover': {
                    borderColor: (t) => alpha(t.palette.primary.main, 0.6),
                    background: (t) => alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.1 : 0.045),
                    transform: 'translateY(-1px)',
                    boxShadow: (t) => `0 4px 14px ${alpha(t.palette.primary.main, 0.14)}`,
                  },
                  '&:active': { transform: 'translateY(0)' },
                  '&:focus-visible': {
                    outline: (t) => `2px solid ${alpha(t.palette.primary.main, 0.5)}`,
                    outlineOffset: 2,
                  },
                }),
              }}
            >
              {/* selection indicator */}
              <Box
                sx={{
                  mt: '1px',
                  flexShrink: 0,
                  display: 'flex',
                  color: (t) => (active ? t.palette.primary.main : t.palette.text.disabled),
                }}
              >
                {multiple ? (
                  active ? (
                    <CheckCircleRoundedIcon sx={{ fontSize: 20 }} />
                  ) : (
                    <RadioButtonUncheckedRoundedIcon sx={{ fontSize: 20 }} />
                  )
                ) : active ? (
                  <CheckCircleRoundedIcon sx={{ fontSize: 20 }} />
                ) : (
                  <RadioButtonUncheckedRoundedIcon sx={{ fontSize: 20 }} />
                )}
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: active ? 700 : 600, lineHeight: 1.4 }}
                >
                  {label}
                </Typography>
                {desc && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ lineHeight: 1.45 }}
                  >
                    {desc}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {multiple && !isDone && (
        <Box sx={{ mt: 1.75, display: 'flex', justifyContent: 'flex-end' }}>
          <Box
            component="button"
            type="button"
            disabled={selected.size === 0}
            onClick={handleSubmitMulti}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              px: 2,
              py: 0.85,
              border: 'none',
              borderRadius: 2,
              cursor: selected.size === 0 ? 'not-allowed' : 'pointer',
              fontSize: '0.85rem',
              fontWeight: 600,
              fontFamily: 'inherit',
              color: (t) =>
                selected.size === 0 ? t.palette.text.disabled : t.palette.primary.contrastText,
              background: (t) =>
                selected.size === 0
                  ? t.palette.action.disabledBackground
                  : t.palette.primary.main,
              transition: 'all 0.18s ease',
              '&:hover': selected.size === 0
                ? {}
                : {
                    background: (t) => t.palette.primary.dark,
                    boxShadow: (t) => `0 4px 14px ${alpha(t.palette.primary.main, 0.35)}`,
                  },
            }}
          >
            <CheckRoundedIcon sx={{ fontSize: 18 }} />
            Conferma ({selected.size})
          </Box>
        </Box>
      )}

      {(isDone && answeredText) && (
        <Box sx={{ mt: 1.75, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            color="success"
            variant="outlined"
            icon={<CheckCircleRoundedIcon />}
            label={answeredText}
            sx={{ fontWeight: 600, borderRadius: 1.5 }}
          />
        </Box>
      )}
    </Paper>
  );
}
